import {
  startWith,
  pairwise,
  filter,
  mergeMap,
  Observable,
  of,
  take,
  withLatestFrom,
  catchError,
  tap,
  delay,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Router } from '@angular/router';

import { Injectable } from '@angular/core';
import {
  AppFacade,
  OfflineFacade,
  UserDetailsFacade,
} from '@shared/state/facades';
import {
  AfterOfflineSyncService,
  CurrentUserDataService,
  DataRefreshService,
  RoutingService,
  UserRightsService,
} from '.';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@shared/components';
import { Constants, getStaticMessage } from '@shared/utils';
import { RefreshType, StaticMessage, SyncErrorType } from '@shared/enums';
import { PersistenceService } from './persistence';
import { HttpErrorResponse } from '@angular/common/http';
import { AppStatusService, AuthService } from '@auth/services';

@UntilDestroy()
@Injectable()
export class OfflineSyncService {
  private isAppOnline$ = this.appFacade.query.isAppOnline$;
  private dprDetailsOfflineChanges$ =
    this.offlineFacade.query.dprDetailsOfflineChanges$;
  private syncOfflineGuid$ = this.offlineFacade.query.syncOfflineGuid$;
  private subprojects$ = this.userDetailsFacade.query.subprojects$;

  constructor(
    private appFacade: AppFacade,
    private offlineFacade: OfflineFacade,
    private userDetailsFacade: UserDetailsFacade,
    private dataRefreshService: DataRefreshService,
    private persistenceService: PersistenceService,
    private currentUserDataService: CurrentUserDataService,
    private userRightsService: UserRightsService,
    private routingService: RoutingService,
    private afterOfflineSyncService: AfterOfflineSyncService,
    private router: Router,
    private dialog: MatDialog,
    private authService: AuthService,
    private appStatusService: AppStatusService
  ) {}

  setup(): void {
    this.isAppOnline$
      .pipe(
        untilDestroyed(this),
        startWith(null),
        pairwise(),
        filter(
          ([previousValue, currentValue]) =>
            previousValue !== null && // skip first emission
            previousValue !== currentValue &&
            currentValue === true
        ),
        // TODO(improvement): This is a temporally as before b2c the logic here
        // introduced an artificial delay; we will need to come up with a better solution
        // to provide an orchestration mechanism between this and the autosave from
        // dpr details page
        delay(500),
        mergeMap(() => this.reloadDataIfOfflineAfterLogin()),
        mergeMap(() => this.syncOfflineData(true))
      )
      .subscribe();

    this.authService.appReInit$
      .pipe(
        untilDestroyed(this),
        filter(() => this.appStatusService.online),
        mergeMap(() => this.checkForOfflineDataToSync())
      )
      .subscribe();
  }

  checkForOfflineDataToSync(): Observable<void> {
    return this.reloadDataIfOfflineAfterLogin().pipe(
      take(1),
      mergeMap(() => this.syncOfflineData(true))
    );
  }
  //#region Private methods

  private reloadDataIfOfflineAfterLogin(): Observable<void> {
    // if the user on dpr list view reconnects, and did not do any changes, reload the page in order to load dprs from server
    return this.dprDetailsOfflineChanges$.pipe(
      take(1),
      withLatestFrom(this.dprDetailsOfflineChanges$),
      mergeMap(([dprDetailsOfflineChanges]) => {
        if (
          !dprDetailsOfflineChanges?.length &&
          this.router.url.includes('dprs')
        ) {
          this.afterOfflineSyncService.processAfterOfflineWithoutSync();
        }
        return of(null);
      })
    );
  }

  private syncOfflineData(fromOffline: boolean): Observable<void> {
    return this.currentUserDataService
      .detectIfSameUserChangedDataOffline()
      .pipe(
        withLatestFrom(this.userDetailsFacade.query.userDetails$),
        mergeMap(([currentAccountHasOfflineChanges, userDetails]) => {
          if (currentAccountHasOfflineChanges) {
            return this.displayUnsavedOfflineChangesDialog();
          }

          // user details check is needed here to avoid triggering an AfterOfflineSync data refresh when logging out
          if (
            fromOffline &&
            this.router.url.includes('dpr-details') &&
            userDetails
          ) {
            this.dataRefreshService.setRefreshNeeded(
              RefreshType.AfterOfflineSync
            );
          }
          return of(null);
        })
      );
  }

  private displayUnsavedOfflineChangesDialog(): Observable<void> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      ...Constants.DialogOptions,
      data: {
        title: $localize`:@@shared.offlineSync.titleBackOnline:You are back online`,
        message: $localize`:@@shared.offlineSync.txtOfflineDataWillSync:You have locally saved data from working offline. The system will now try to synchronize the data to the server.`,
        buttonLabel: getStaticMessage(StaticMessage.OkButton),
        hideCancelButton: true,
      },
    });

    return dialogRef.afterClosed().pipe(
      filter((result) => !!result),
      withLatestFrom(
        this.syncOfflineGuid$,
        this.dprDetailsOfflineChanges$,
        this.subprojects$
      ),
      mergeMap(([, syncOfflineGuid, dprDetailsOfflineChanges, subprojects]) =>
        this.persistenceService.syncOfflineData(
          syncOfflineGuid,
          dprDetailsOfflineChanges,
          subprojects?.find(
            (subproject) =>
              subproject.id === dprDetailsOfflineChanges[0]?.dprSubprojectId
          )
        )
      ),
      mergeMap((response) => {
        this.offlineFacade.setDprDetailsOfflineChanges(null);
        this.offlineFacade.deleteSyncOfflineGuid();
        return of(response);
      }),
      mergeMap((response) => {
        if (!response?.length) {
          return this.processAfterOfflineSync();
        }

        let errorInfo: string;

        let result$: Observable<void> = of(null);

        response.forEach((item, index) => {
          if (item.syncErrorType === SyncErrorType.PartialSync) {
            if (item.errorDetails) {
              if (item.errorDetails.key) {
                item.errorMessage += `</br></br><b>${item.errorDetails.key}</b></br>`;
              }

              if (item.errorDetails.value?.length) {
                item.errorDetails.value.forEach((detail) => {
                  const revision = `</br><b>${getStaticMessage(
                    StaticMessage.RevisionText
                  )}: ${detail.key}</b></br>`;
                  errorInfo = `${detail.value.join('</br>')}</br></br>`;
                  item.errorMessage += revision.concat(errorInfo);
                });
              }
            }

            if (item.isDprUnlocked) {
              item.errorMessage = item.errorMessage.concat(
                $localize`:@@shared.offlineSync.errDprWasNotLocked:DPR was not locked, as not all data was synchronized with the server.`
              );
            }
          }

          const errorDialogRef = this.dialog.open(ConfirmDialogComponent, {
            ...Constants.DialogOptions,
            data: {
              title: item.errorTitle,
              message: item.errorMessage,
              buttonLabel: getStaticMessage(StaticMessage.OkButton),
              hideCancelButton: true,
            },
          });

          if (index === 0) {
            result$ = errorDialogRef
              .afterClosed()
              .pipe(mergeMap(() => this.processAfterOfflineSync()));
          }
        });

        return result$;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 403) {
          const noPermissionsErrorDialogRef = this.dialog.open(
            ConfirmDialogComponent,
            {
              ...Constants.DialogOptions,
              data: {
                title: getStaticMessage(StaticMessage.ErrorTitle),
                message: $localize`:@@shared.offlineSync.errSyncErrorNoPermissions:Data could not be synced with the server. You don't have permissions.`,
                buttonLabel: getStaticMessage(StaticMessage.OkButton),
                hideCancelButton: true,
              },
            }
          );

          noPermissionsErrorDialogRef
            .afterClosed()
            .pipe(
              untilDestroyed(this),
              tap(() => {
                this.afterOfflineSyncService.processAfterOfflineWithoutSync();
              })
            )
            .subscribe();
        }

        return of(null);
      })
    );
  }

  private processAfterOfflineSync(): Observable<void> {
    if (this.router.url.includes('dprs')) {
      this.offlineFacade.setDprDetails(null);
      this.afterOfflineSyncService.processAfterOfflineWithoutSync();
      return of(null);
    } else {
      return this.currentUserDataService
        .getUserDetails(this.routingService.routePrefix)
        .pipe(
          take(1),
          mergeMap((userSession) => {
            return userSession.selectedSubproject &&
              this.userRightsService.hasRightsForSubprojectState(
                'dprs',
                userSession.selectedSubproject.id,
                userSession
              )
              ? this.persistenceService.action.getLastThreeDprs()
              : of(null);
          }),
          mergeMap((lastThreeDprs) => {
            this.offlineFacade.setDprDetailsList(lastThreeDprs);

            this.dataRefreshService.setRefreshNeeded(
              RefreshType.AfterOfflineSync
            );
            return of(null);
          })
        );
    }
  }

  //#endregion
}
